/* eslint-disable no-param-reassign */
import { message as antdMessage } from 'antd';
import axios from 'axios';
import parsedPhoneNumber from 'libphonenumber-js';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import pick from 'lodash/pick';
import type { SagaIterator } from 'redux-saga';
import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';

import { userMembershipClient } from 'clients';
import { InternalAdminRole } from 'constants/internal-admin-roles';
import { setBackOfficeUser } from 'store/modules/backOfficeUser/actions';
import Enterprise from 'types/models/enterprise';
import Membership from 'types/models/membership';
import User from 'types/models/user';
import { APIResponseParsed } from 'types/response';
import parseUserPhones from 'utils/phone-number/parse-user-phones';
import { getRequest, patchRequest } from 'utils/redux-saga-requests';

import {
  editBackOfficeUser,
  editBackOfficeUserFailure,
  editBackOfficeUserSuccess,
  loadBackOfficeUsers,
  loadBackOfficeUsersFailure,
  loadBackOfficeUsersSuccess,
  removeInternalAdminRole,
  removeInternalAdminRoleFailure,
  removeInternalAdminRoleSuccess,
  setBackOfficeUsers
} from './actions';
import ActionTypes from './constants';
import selectBackOfficeUser from '../backOfficeUser/selectors';

function getPhoneNumberValue(
  values: Record<string, unknown>,
  number: string,
  countryCode: string
) {
  return values
    ? {
      [number]: values[number] && values[countryCode]
        ? parsedPhoneNumber(`${values[countryCode]}${values[number]}`)?.number
        : null
    }
    : {};
}

export function* requestBackOfficeUsers(action: ReturnType<typeof loadBackOfficeUsers>): SagaIterator {
  const { parameters: params } = action.payload;
  try {
    const { data } = yield call(getRequest, 'user', { params });
    yield all([
      put(loadBackOfficeUsersSuccess(data)),
      put(
        setBackOfficeUsers({
          total: data.total,
          data: keyBy(
            data.data.map(
              (user: User): User => parseUserPhones(user)
            ),
            'id'
          ),
          ids: data.data.map((user: User) => user.id)
        })
      )
    ]);
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    const message =
      error.message === 'Network Error'
        ? error.message
        : get(error, 'response.data.message', 'Sorry, something went wrong.');

    yield put(loadBackOfficeUsersFailure(message, error));
  }
}

export function* watchLoadBackOfficeUsersRequest() {
  yield takeLatest(
    ActionTypes.LOAD_BACK_OFFICE_USERS_REQUEST,
    requestBackOfficeUsers
  );
}

export function* requestEditBackOfficeUser(
  action: ReturnType<typeof editBackOfficeUser>
): SagaIterator {
  const {
    payload: { userId, values, onSuccess }
  } = action;

  try {
    const mobileNumberValue = getPhoneNumberValue(
      values,
      'mobileNumber',
      'mobileNumberCountryCode'
    );
    const landlineNumberValue = getPhoneNumberValue(
      values,
      'landlineNumber',
      'landlineNumberCountryCode'
    );

    // Collect all ids of deleted memberships.
    const oldBackOfficeUser = yield select(selectBackOfficeUser);
    const oldMemberships: Membership[] = oldBackOfficeUser.memberships || [];
    const newMemberships: Membership[] = values.memberships || [];

    const oldMembershipIds = oldMemberships.map((m) => m.id).filter((m) => !!m);
    const newMembershipIds = newMemberships.map((m) => m.id).filter((m) => !!m);

    const newMembershipEnterpriseIds = newMemberships.map(
      (m) => m.enterpriseId
    );

    // Get the intersection of old & new memberships.
    // Two memberships are the same if they have the same enterprise ID
    // (we don't allow a user to have two memberships to the same enterprise.)
    const mergedMemberships = [
      ...newMemberships,
      ...(oldMemberships.filter(
        (m) => !newMembershipEnterpriseIds.includes(m.enterpriseId)
      ) || [])
    ];

    // Store old membership's enterprise names in memory for later use
    const enterpriseNames = oldMemberships.reduce<Record<Enterprise['id'], Enterprise['name']>>((acc, membership) => {
      if (membership.enterprise) {
        acc[membership.enterpriseId] = membership.enterprise.name;
      }
      return acc;
    }, {});

    // Strip unneeded properties.
    mergedMemberships.forEach((membership: Membership) => {
      delete membership.enterprise;
    });

    if (values.memberships) {
      values.memberships = mergedMemberships;
    }
    const adminRole: InternalAdminRole = values.adminRole ?? InternalAdminRole._NONE;
    const { data } = yield call(patchRequest, `user/${userId}`, {
      ...values,
      adminRole,
      // Pass phone number in format needed by backend.
      ...mobileNumberValue,
      ...landlineNumberValue
    });
    const deletedMembershipIds = oldMembershipIds.filter(
      (oldId) => !newMembershipIds.includes(oldId)
    );

    // Make DELETE request to user-membership endpoint for each deleted
    // membership.
    const deleteResponses: APIResponseParsed<string>[] = yield call(
      userMembershipClient.deleteUserMemberships,
      deletedMembershipIds
    );

    deleteResponses.forEach((deleteResponse, index) => {
      const oldMembership = oldMemberships.find((m) => m.id === deletedMembershipIds[index]);
      // This shouldn't happen
      if (!oldMembership) {
        return;
      };

      const enterpriseName = enterpriseNames[oldMembership.enterpriseId] ?? 'enterprise'; // Fallback is unlikely to be used
      if (deleteResponse.data) {
        antdMessage.success(`Successfully removed membership from ${enterpriseName}`);
      } else if (deleteResponse.error) {
        antdMessage.error(`Failed to remove membership from ${enterpriseName}: ${deleteResponse.error.message}`);
      }
    });

    yield put(
      setBackOfficeUser(parseUserPhones(data))
    );

    yield put(editBackOfficeUserSuccess(data));

    if (onSuccess) onSuccess();
    antdMessage.success('User edited');
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    const message = get(
      error,
      'response.data.message',
      'Sorry, something went wrong.'
    );

    antdMessage.error('Failed to edit user');
    yield put(editBackOfficeUserFailure(message, error));
  }
}

export function* requestRemoveInternalAdminRole(
  action: ReturnType<typeof removeInternalAdminRole>
): SagaIterator {
  const { payload: { userId, onSuccess } } = action;

  const oldBackOfficeUser = yield select(selectBackOfficeUser);
  /** These attributes are needed to pass API validation */
  const requiredAttributes = pick(oldBackOfficeUser, ['username', 'firstName', 'lastName', 'email']);

  try {
    const { data } = yield call(patchRequest, `user/${userId}`, {
      ...requiredAttributes,
      adminRole: InternalAdminRole._NONE
    });
    yield all([
      put(setBackOfficeUser(parseUserPhones(data))),
      put(removeInternalAdminRoleSuccess())
    ]);

    if (onSuccess) onSuccess();
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;

    const message = get(error, 'response.data.message', 'Sorry, something went wrong.');
    antdMessage.error('Failed to remove internal admin role');
    yield put(removeInternalAdminRoleFailure(message));
  }
}

export function* watchEditBackOfficeUsersRequest() {
  yield takeLatest(
    ActionTypes.EDIT_BACK_OFFICE_USER_REQUEST,
    requestEditBackOfficeUser
  );
}

export function* watchRemoveInternalAdminRoleRequest() {
  yield takeLatest(
    ActionTypes.REMOVE_INTERNAL_ADMIN_ROLE_REQUEST,
    requestRemoveInternalAdminRole
  );
}

export default function* backOfficeUsersSaga() {
  yield all([
    fork(watchLoadBackOfficeUsersRequest),
    fork(watchEditBackOfficeUsersRequest),
    fork(watchRemoveInternalAdminRoleRequest)
  ]);
}
